import { useState, useEffect, useRef } from 'react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { HelpCircle, ChevronDown, ChevronUp } from 'lucide-react'

gsap.registerPlugin(ScrollTrigger)

const faqs = [
  {
    question: 'How is Pelajari different from our existing HRIS?',
    answer:
      'Most HRIS tools manage HR admin — payroll, leave, org charts. Pelajari is built on top of that to handle the things HRIS typically ignores: structured competency tracking, psychometric assessments, AI-powered talent intelligence, and competency-matched hiring. Think of it as the intelligence layer that sits alongside your HRIS, not a replacement for it.',
  },
  {
    question: 'Do we need IT support or a long implementation?',
    answer:
      'No. Pelajari is a self-serve SaaS platform. Most companies are fully set up within 1–2 weeks. You create your org structure, invite employees, and the platform guides you through each module. There is no on-premise installation, no custom code, and no need to involve your IT team.',
  },
  {
    question: 'Is the AI actually useful or just a buzzword?',
    answer:
      'It is specific and functional. The AI does four concrete things today: (1) extracts competencies from candidate CVs and scores them against job requirements, (2) automatically places every employee on the 9-Box talent grid based on real assessment data, (3) runs competency gap analysis comparing employee profiles to position requirements, and (4) generates structured talent reports with development recommendations. These are not generic suggestions — they are grounded in your actual data.',
  },
  {
    question: 'Can employees see their own scores and assessments?',
    answer:
      'Yes. Every employee has a personal dashboard showing their competency scores, assessment history, psychometric test results, and IDP progress. Transparency is intentional — employees who can see their own data are more motivated to develop. HR and managers see the full team view; employees see only their own profile.',
  },
  {
    question: 'What kind of documents can I upload?',
    answer:
      'Today you can upload candidate CVs as text for AI competency extraction during recruitment. Coming soon: upload performance reviews, competency frameworks, and training documents for AI-powered gap analysis and personalized learning path generation.',
  },
  {
    question: 'Why do I need this if I already have an LMS?',
    answer:
      'An LMS manages learning content. Pelajari manages the performance and talent intelligence that tells you what learning is actually needed. Without competency data, your LMS serves generic content. With Pelajari, learning recommendations are tied to real competency gaps identified through assessments — so your LMS investment becomes far more effective.',
  },
  {
    question: 'Is this platform suitable for our company size?',
    answer:
      'Yes. Pelajari scales from lean teams of 15 people to large enterprises with thousands of employees. The Starter plan covers small teams with core performance and assessment features. Growth and Enterprise tiers unlock advanced AI reports, unlimited roles, and priority support. Pricing scales with your headcount, not with feature paywalls.',
  },
  {
    question: 'How is our data handled and kept private?',
    answer:
      'Each company operates in a fully isolated instance — your data is never shared with or visible to other organizations. All data is encrypted at rest and in transit. Employee assessment scores and talent reports are access-controlled by role: employees see their own data, HR sees the full team, and managers see their direct reports. We do not use your data to train AI models.',
  },
]

const leftFaqs = faqs.filter((_, i) => i % 2 === 0)
const rightFaqs = faqs.filter((_, i) => i % 2 === 1)

function AccordionItem({
  faq,
  isOpen,
  onToggle,
}: {
  faq: (typeof faqs)[number]
  isOpen: boolean
  onToggle: () => void
}) {
  return (
    <div className="border-b border-[#e6e7e7]">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-start justify-between text-left text-base font-medium text-[#000f0f] py-5 hover:text-[#359d9e] transition-colors gap-4"
      >
        <span className="leading-snug">{faq.question}</span>
        {isOpen
          ? <ChevronUp className="w-5 h-5 flex-shrink-0 text-[#359d9e] mt-0.5" />
          : <ChevronDown className="w-5 h-5 flex-shrink-0 text-[#b3b7b7] mt-0.5" />
        }
      </button>
      {isOpen && (
        <div className="text-[15px] text-[#1a2727] leading-relaxed pb-5">
          {faq.answer}
        </div>
      )}
    </div>
  )
}

function AccordionColumn({ items }: { items: typeof faqs }) {
  const [openIndex, setOpenIndex] = useState<number | null>(0)
  return (
    <div className="flex flex-col">
      {items.map((faq, i) => (
        <AccordionItem
          key={faq.question}
          faq={faq}
          isOpen={openIndex === i}
          onToggle={() => setOpenIndex(openIndex === i ? null : i)}
        />
      ))}
    </div>
  )
}

export function LandingFAQ() {
  const sectionRef = useRef<HTMLElement>(null)
  const headerRef = useRef<HTMLDivElement>(null)
  const leftColRef = useRef<HTMLDivElement>(null)
  const rightColRef = useRef<HTMLDivElement>(null)
  const mobileColRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Header fade-up
      gsap.from(headerRef.current, {
        y: 24,
        opacity: 0,
        duration: 0.7,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: headerRef.current,
          start: 'top 85%',
          once: true,
        },
      })

      // Desktop columns fade-up with stagger
      ;[leftColRef.current, rightColRef.current].forEach((col, i) => {
        if (!col) return
        gsap.from(col, {
          y: 20,
          opacity: 0,
          duration: 0.6,
          delay: i * 0.12,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: col,
            start: 'top 88%',
            once: true,
          },
        })
      })

      // Mobile column fade-up
      if (mobileColRef.current) {
        gsap.from(mobileColRef.current, {
          y: 20,
          opacity: 0,
          duration: 0.6,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: mobileColRef.current,
            start: 'top 88%',
            once: true,
          },
        })
      }
    }, sectionRef)

    return () => ctx.revert()
  }, [])

  return (
    <section ref={sectionRef} className="flex flex-col items-center gap-12 px-6 py-16 md:px-10 lg:px-[120px] max-w-screen-xl mx-auto">
      {/* Header */}
      <div ref={headerRef} className="flex flex-col items-center gap-3 text-center sm:flex-row sm:gap-4">
        <div className="w-12 h-12 bg-[#f2fbfa] rounded-full flex items-center justify-center flex-shrink-0">
          <HelpCircle className="w-6 h-6 text-[#359d9e]" />
        </div>
        <div className="text-center sm:text-left">
          <h2 className="text-[30px] font-semibold leading-[38px] tracking-[-0.5px] text-[#000f0f] sm:text-[32px] sm:leading-[40px] lg:text-[36px] lg:leading-[45px] lg:tracking-[-0.75px]">
            Frequently Asked Questions
          </h2>
          <p className="text-sm text-[#b3b7b7] mt-0.5">Everything you need to know before getting started</p>
        </div>
      </div>

      {/* Two-column accordion (desktop) / single column (mobile) */}
      <div className="w-full max-w-[1100px]">
        <div className="hidden lg:grid lg:grid-cols-2 lg:gap-x-16">
          <div ref={leftColRef}><AccordionColumn items={leftFaqs} /></div>
          <div ref={rightColRef}><AccordionColumn items={rightFaqs} /></div>
        </div>
        <div ref={mobileColRef} className="lg:hidden">
          <AccordionColumn items={faqs} />
        </div>
      </div>

      {/* Footer nudge */}
      <p className="text-sm text-[#b3b7b7]">
        Still have questions?{' '}
        <a
          href="mailto:support@pelajari.ai"
          className="text-[#359d9e] font-medium hover:underline"
        >
          support@pelajari.ai
        </a>
      </p>
    </section>
  )
}
